import { z } from 'zod';
import { DEFAULT_LOCALE, IdSchema, LocaleSchema } from './common';
import { DocumentKindSchema, DocumentStatusSchema } from './documents';

export const UserSchema = z.object({
  id: IdSchema,
  email: z.string(),
  fullName: z.string(),
  role: z.enum(['OWNER', 'ADMIN', 'MANAGER', 'STOREKEEPER', 'CASHIER', 'VIEWER']),
  locale: LocaleSchema,
  isActive: z.boolean(),
  lastLoginAt: z.string().nullable(),
  createdAt: z.string(),
});
export type User = z.infer<typeof UserSchema>;

export const CreateUserSchema = z.object({
  email: z.email().transform((value) => value.toLowerCase()),
  fullName: z.string().trim().min(1).max(200),
  password: z.string().min(8).max(200),
  role: z.enum(['OWNER', 'ADMIN', 'MANAGER', 'STOREKEEPER', 'CASHIER', 'VIEWER']).default('CASHIER'),
  locale: LocaleSchema.default(DEFAULT_LOCALE),
});

export const UpdateUserSchema = z.object({
  fullName: z.string().trim().min(1).max(200).optional(),
  role: z.enum(['OWNER', 'ADMIN', 'MANAGER', 'STOREKEEPER', 'CASHIER', 'VIEWER']).optional(),
  locale: LocaleSchema.optional(),
  isActive: z.boolean().optional(),
  password: z.string().min(8).max(200).optional(),
});
export type CreateUser = z.infer<typeof CreateUserSchema>;
export type UpdateUser = z.infer<typeof UpdateUserSchema>;

export const DashboardSummarySchema = z.object({
  stock: z.object({
    totalValue: z.string(),
    totalSkus: z.number().int().nonnegative(),
    totalQuantity: z.string(),
    lowStockCount: z.number().int().nonnegative(),
    outOfStockCount: z.number().int().nonnegative(),
  }),
  documents: z.object({
    drafts: z.number().int().nonnegative(),
    postedThisMonth: z.number().int().nonnegative(),
    amountThisMonth: z.string(),
  }),
  catalog: z.object({
    products: z.number().int().nonnegative(),
    categories: z.number().int().nonnegative(),
    warehouses: z.number().int().nonnegative(),
    counterparties: z.number().int().nonnegative(),
  }),
  /** 14-day movement sparkline for the dashboard chart. */
  movementTrend: z.array(
    z.object({
      date: z.string(),
      inbound: z.string(),
      outbound: z.string(),
    }),
  ),
  lowStock: z.array(
    z.object({
      productId: IdSchema,
      sku: z.string(),
      name: z.string(),
      quantity: z.string(),
      minStock: z.string(),
      unitShortName: z.string(),
    }),
  ),
  recentDocuments: z.array(
    z.object({
      id: IdSchema,
      number: z.string(),
      kind: DocumentKindSchema,
      status: DocumentStatusSchema,
      date: z.string(),
      counterpartyName: z.string().nullable(),
      totalAmount: z.string(),
    }),
  ),
});
export type DashboardSummary = z.infer<typeof DashboardSummarySchema>;
